import React, { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import duration from 'dayjs/plugin/duration';

import { MarketTypeV2, TopEvent } from '@atlas/components';
import {
  ApiManager,
  BetManager,
  constants,
  DateService,
  isEventOffline,
  isSuspended,
  ODDS_HIGHLIGHTED_STATUS,
  OutcomeService,
  SiteConfigManager,
  useBetslipState,
  useHighlightedOutcomes,
  useLabel,
  useSettings,
} from '@atlas/core';
import { EventLink } from '@atlas/routing';
import { Icon, Label, useEventViewHandlers } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';

dayjs.extend(calendar);
dayjs.extend(duration);

const SoloGames = [2, 7, 10, 11, 14, 16, 22, 23, 24, 29, 34, 37, 40, 52, 53, 55, 71];
const { SPORT_SERVICE } = constants;

const EVENT_PROPERTIES = {
  NON_MARGIN: 'NON_MARGIN',
  ODDS_BOOST: 'ODDS_BOOST',
  ONLY_SINGLE: 'ONLY_SINGLE',
};

const mapToCommonEvent = ({ event, marketId, marketTypeId }) => ({
  ...event,
  marketTypes: {
    [marketTypeId]: {
      id: marketTypeId,
      markets: {
        [marketId]: {
          ...event.market,
          outcomes: event.market.outcomes.reduce((acc, item) => ({ ...acc, [item.id]: item }), {}),
          status: event.market.status,
        },
      },
      name: event.marketTypeName,
    },
  },
  onlySingle: event.properties.includes(EVENT_PROPERTIES.ONLY_SINGLE),
});

const eventClickHandler = payload => {
  const { event, marketId, marketTypeId, outcomeId } = payload;

  if (outcomeId && event) {
    BetManager.selectBet({
      ...payload,
      event: mapToCommonEvent({ event, marketId, marketTypeId }),
    });
  }
};

const TopEventItem = ({ event }) => {
  const t = useTranslate();

  const {
    settings: { feeds },
  } = useBetslipState();

  const {
    betGroups,
    categoryName,
    eventId,
    info: {
      score: { awayScore, homeScore, matchClock },
    },
    market,
    participants,
    properties,
    source,
    sportId,
    sportName,
    sportService,
    startTime,
    tournamentName,
  } = event;
  const nonMargin = properties.includes(EVENT_PROPERTIES.NON_MARGIN);
  const oddsBoost = properties.includes(EVENT_PROPERTIES.ODDS_BOOST);
  const isEventSuspended =
    isEventOffline({ feeds, source, sportId, sportService }) || isSuspended(event);

  const highlightedOutcomes = useHighlightedOutcomes();
  const { oddsFixed, oddsFormat, oddsFractionDigits, oddsZeroes } = useSettings();
  const prematchSubtitle = dayjs(startTime).calendar(null, {
    nextDay: '[tomorrow]',
    nextWeek: 'dddd',
    sameDay: '[today]',
    sameElse: DateService.getFormattedDate(startTime),
  });
  const translatedPrematchDate = useLabel(prematchSubtitle);
  const onBetSelectHandler = useEventViewHandlers({ ...event, id: eventId }, eventClickHandler);

  const isLogo = SiteConfigManager.getConfig('config.config.general.showLogos', true);
  const isBadge = SiteConfigManager.getConfig('config.config.general.showBadges', true);
  const isSoloGame = SoloGames.includes(sportId);

  const selectedOutcomes = useMemo(
    () =>
      Object.entries(highlightedOutcomes).reduce((acc, [key, value]) => {
        if (
          (value & ODDS_HIGHLIGHTED_STATUS.SELECTED) > 0 ||
          (value & ODDS_HIGHLIGHTED_STATUS.LOADING) > 0 ||
          (value & ODDS_HIGHLIGHTED_STATUS.FAILED) > 0
        ) {
          acc.push(Number(key));
        }

        return acc;
      }, []),
    [highlightedOutcomes],
  );

  const dateTime = useMemo(() => {
    if (sportService === SPORT_SERVICE.PREMATCH) {
      const prematchTime = DateService.getFormattedTime(startTime);
      const prematchDate = prematchSubtitle.match(/^[0-9.,]+$/)
        ? prematchSubtitle
        : translatedPrematchDate;

      return `${prematchDate} | ${prematchTime}`;
    }

    const getTime = () => {
      const numberMatchClock = Number(matchClock);

      if (numberMatchClock <= 0) {
        return null;
      }

      const durationTime = dayjs.duration(numberMatchClock, 'seconds');
      const minutes = Math.floor(durationTime.asMinutes()).toString().padStart(2, '0');
      const seconds = durationTime.seconds().toString().padStart(2, '0');

      return `${minutes}:${seconds}`;
    };

    const time = getTime();
    const timeString = time ? `${time} | ` : '';

    return `${t('live')} | ${timeString}${homeScore}:${awayScore}`;
  }, [
    awayScore,
    homeScore,
    matchClock,
    prematchSubtitle,
    sportService,
    startTime,
    t,
    translatedPrematchDate,
  ]);

  const eventParticipants = useMemo(
    () =>
      [participants.home, participants.away].map(({ fullName, id, imagePaths }) => {
        let logo;
        const imagePath = imagePaths?.ICON48 || '/participant.png';

        if (!isLogo && !isBadge) {
          logo = null;
        } else if (isSoloGame || (!isLogo && isBadge)) {
          logo = (
            <Icon
              alt={fullName}
              className="event-participant-logo__img event-participant-logo__img--sport"
              name={`sportIcon-${sportId}`}
              type="sport"
            />
          );
        } else {
          logo = (
            <img alt="Logo" loading="lazy" src={`${ApiManager.getHostname('cdn')}${imagePath}`} />
          );
        }

        return {
          id,
          logo,
          name: fullName,
        };
      }),
    [isBadge, isLogo, isSoloGame, participants, sportId],
  );

  const formatOdds = useCallback(
    value =>
      OutcomeService.formatOdds(value, {
        fixed: oddsFixed,
        fractionDigits: oddsFractionDigits,
        oddsFormat,
        zeroes: oddsZeroes,
      }),
    [oddsFixed, oddsFormat, oddsFractionDigits, oddsZeroes],
  );

  const marketAllButtonTitle = <Label message="top_events_see_all_markets" />;

  return (
    <div className="top-event-wrapper">
      <EventLink eventId={eventId}>
        <TopEvent
          country={categoryName}
          dateTime={dateTime}
          eventName={tournamentName}
          noneMargin={nonMargin}
          oddsBoost={oddsBoost}
          participants={eventParticipants}
          sportName={sportName}
        >
          <MarketTypeV2
            betGroups={betGroups}
            formatOdds={formatOdds}
            market={market}
            marketAllButtonTitle={marketAllButtonTitle}
            onClick={onBetSelectHandler}
            selectedOutcomes={selectedOutcomes}
            suspended={isEventSuspended}
          />
        </TopEvent>
      </EventLink>
    </div>
  );
};

export default TopEventItem;
